import { ICustomFormField, IDynamicFormTemplate, ITemplateByCodeResponse } from "./CustomFormFields.types";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { api } from "../../api/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { ERROR_MESSAGES } from "../../constants/enums";

export const fetchCustomFormFieldsList = createAsyncThunk<
  {
    data: ICustomFormField[];
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: APIError }
>(
  "customFormFields/fetchCustomFormFieldsList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `/policy/get-questionnaire?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/policy/get-questionnaire?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: ICustomFormField[] = res.data.data;
      return {
        data,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertCustomFormField = createAsyncThunk<
  {},
  { data: ICustomFormField },
  { rejectValue: APIError }
>(
  "customFormFields/upsertCustomFormField",
  async ({ data }, { rejectWithValue }) => {
    try {
      await api.post("/policy/upsert-questionnaire", {
        ...data,
      });
      return {};
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchCustomFormFieldsByType = async (
  type: string,
): Promise<ICustomFormField[]> => {
  try {
    const res = await api.get(
      `/policy/get-questionnaire?column=type&value=${type}&status=ACTIVE`,
    );
    return res.data.data as ICustomFormField[];
  } catch {
    return [];
  }
};

export const fetchDynamicFormTemplatesList = createAsyncThunk<
  {
    data: IDynamicFormTemplate[];
  },
  {
    pageNumber: number;
    rowsInPerPage: number;
    limit: number;
    status: string;
    date: IDataTableV2DateState["dates"];
    searchValue: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: APIError }
>(
  "customFormFields/fetchDynamicFormTemplatesList",
  async (
    { pageNumber, rowsInPerPage, limit, status, date, searchValue },
    { rejectWithValue },
  ) => {
    try {
      let finalUrl = `/policy/get-questionnaire-template?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/policy/get-questionnaire-template?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: IDynamicFormTemplate[] = res.data.data;
      return {
        data,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const upsertDynamicFormTemplate = createAsyncThunk<
  {},
  { data: IDynamicFormTemplate },
  { rejectValue: APIError }
>(
  "customFormFields/upsertDynamicFormTemplate",
  async ({ data }, { rejectWithValue }) => {
    try {
      await api.post("/policy/upsert-questionnaire-template", {
        ...data,
      });
      return {};
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchAllQuestionnaires = createAsyncThunk<
  { data: ICustomFormField[] },
  void,
  { rejectValue: APIError }
>(
  "customFormFields/fetchAllQuestionnaires",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/policy/get-questionnaire?status=ACTIVE");
      const data: ICustomFormField[] = res.data.data;
      return { data };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchTemplateByCode = async (
  templateCode: string,
): Promise<ITemplateByCodeResponse | null> => {
  try {
    const res = await api.get(
      `/policy/get-questionnaire-template-by-code?template_code=${templateCode}`,
    );
    return res.data.data as ITemplateByCodeResponse;
  } catch {
    return null;
  }
};
